import React, { useEffect, useRef, useState } from 'react';

import {
  watchImpressions,
  watchTimings,
  getImpressionId,
  Watcher,
  TimingWatcher,
  Options,
} from './watchImpressions';
import { useImpressionWatcherContext } from './ImpressionWatcherContext';

export type Props = Options & {
  // Allow external styling of the impression watcher container
  className?: string;
  style?: React.CSSProperties;
  // Allow adding extra params to links the impression watcher is watching
  extraParams?: string;
  // Short-circuiting mechanism to allow conditional disabling of the impression watcher
  active?: boolean;
  // Whether to track timing data for the impression watcher
  timing?: boolean;
  // Stop clicks from propagating to parents
  stopPropagation?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  shouldTrackClick?: (event: React.MouseEvent<HTMLElement>) => boolean;
  additionalTracking?: Options;
  // Callback when an impression is logged
  onImpression?: () => void;
  // Allow rendering as a different element or component
  as?: React.ElementType;
  children?: React.ReactNode;
};

const addExtraParamsToLink = (
  link: HTMLAnchorElement,
  extraParams?: string,
) => {
  const originalUrl = link.href;
  const separator = originalUrl.includes('?') ? '&' : '?';
  let newUrl = originalUrl;
  if (extraParams && !newUrl.includes(extraParams)) {
    newUrl += separator + extraParams;
  }
  link.href = newUrl;
  setTimeout((t) => (t.href = originalUrl), 100, link);
};

/**
 * A wrapper that handles sending impressions data if its children come into view.
 * Also handles adding extra parameters to descendant links, via the `extraParams` prop.
 *
 * NOTE: Nesting an absolutely positioned element inside of this watcher may cause
 * issues getting the IntersectionObserver to fire correctly. If you need an absolute
 * element, use astroturf to wrap the ImpressionWatcher itself:
 *
 * const MyComponent = styled(ImpressionWatcher)`
 *   position: absolute;
 *   // other properties...
 * `;
 */
export const ImpressionWatcher: React.FC<Props> = ({
  className,
  children,
  active = true,
  timing = false,
  stopPropagation: propStopPropagation,
  impressionId,
  style,
  onClick = () => null,
  shouldTrackClick,
  additionalTracking,
  onImpression,
  as: Component = 'div',
  ...options
}) => {
  const container = useRef<HTMLElement | null>(null);
  const watcher = useRef<Watcher | null>(null);
  const additionalWatcher = useRef<Watcher | null>(null);
  const timeWatcher = useRef<TimingWatcher | null>(null);
  const [defaultImpressionId] = useState(getImpressionId);
  const resolvedImpressionId = impressionId ?? defaultImpressionId;
  const { stopPropagation: contextStopPropagation } =
    useImpressionWatcherContext();

  // Merge context and prop values, with prop taking precedence
  const shouldStopPropagation = propStopPropagation ?? contextStopPropagation;

  // Set up impression watcher
  useEffect(() => {
    if (!active) return;
    const cleanup = () => {
      watcher.current?.stop();
      additionalWatcher.current?.stop();
      timeWatcher.current?.stop();
    };

    if (container.current == null) return cleanup;

    const impressionWatcher = watchImpressions(container.current, {
      impressionId: resolvedImpressionId,
      onImpression: () => {
        onImpression?.();
      },
      ...options,
    });
    watcher.current = impressionWatcher;
    additionalWatcher.current = additionalTracking
      ? watchImpressions(container.current, additionalTracking)
      : null;

    if (timing) {
      timeWatcher.current = watchTimings(container.current, {
        ...options,
      });
    }

    return cleanup;
  }, [
    active,
    additionalTracking,
    timing,
    container,
    options,
    watcher,
    resolvedImpressionId,
    onImpression,
  ]);

  // Set up link modification
  // TODO: Does this make sense in the ImpressionWatcher?
  // This is discrete logic that just modifies links, it might be better
  // to be in its own component.
  useEffect(() => {
    if (!active) return;
    const handler = (e: MouseEvent) => {
      if (e.target && e.target instanceof Element) {
        const anchor = e.target.closest('a');
        if (anchor && options.extraParams) {
          addExtraParamsToLink(anchor, options.extraParams);
        }
      }
    };

    // React gets rid of refs before cleanup, so we need to copy this into a variable
    const el = container.current;

    el?.addEventListener('click', handler);
    return () => el?.removeEventListener('click', handler);
  }, [active, options.extraParams]);

  return (
    <Component
      ref={(ref: HTMLElement | null) => {
        if (active) {
          container.current = ref;
        }
      }}
      style={style}
      className={className}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        if (active && (shouldTrackClick?.(e) ?? true)) {
          watcher.current?.click();
          additionalWatcher.current?.click();
        }
        if (shouldStopPropagation) {
          e.stopPropagation();
        }
        onClick(e);
      }}
    >
      {children}
    </Component>
  );
};

export default ImpressionWatcher;
